import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TablePagination,
  TextField,
  IconButton,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  CircularProgress,
} from "@mui/material";
import { Edit, Delete, PictureAsPdf } from "@mui/icons-material";
import logo from "./logoo.png";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from "recharts";
import jsPDF from "jspdf";
import Swal from "sweetalert2";
import api from "../../api/api";

const MonthlyFinancialRecord = () => {
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [rowsPerPage] = useState(6);
  const [search, setSearch] = useState("");
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editRecord, setEditRecord] = useState(null);
  const [donationIncome, setDonationIncome] = useState("");

  // New record states
  const [newMonth, setNewMonth] = useState("");
  const [newYear, setNewYear] = useState("");
  const [newDonation, setNewDonation] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    fetchRecords();
  }, []);

  const fetchRecords = async () => {
    try {
      const res = await api.get("/financial-records/");
      setRecords(res.data);
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async () => {
    if (!newMonth || !newYear) {
      Swal.fire("Missing fields", "Month and Year are required", "warning");
      return;
    }
    setSubmitting(true);
    setTimeout(async () => {
      try {
        await api.post("/financial-records/", {
          month: newMonth,
          year: parseInt(newYear),
          donation_income: newDonation || 0,
        });
        fetchRecords();
        setNewMonth("");
        setNewYear("");
        setNewDonation("");
        Swal.fire("Success", "Record added successfully", "success");
      } catch (error) {
        console.error("Error creating record", error);
        Swal.fire("Error", "Failed to create record", "error");
      } finally {
        setSubmitting(false);
      }
    }, 3000);
  };

  const handleDelete = (id) => {
    Swal.fire({
      title: "Confirm Delete",
      text: "Are you sure you want to delete this record?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#3085d6",
      confirmButtonText: "Yes, delete it!",
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          await api.delete(`/financial-records/${id}/`);
          fetchRecords();
          Swal.fire("Deleted!", "The record has been deleted.", "success");
        } catch (error) {
          Swal.fire("Error", "Failed to delete record.", "error");
        }
      }
    });
  };

  const handleEditClick = (record) => {
    setEditRecord(record);
    setDonationIncome(record.donation_income);
    setEditDialogOpen(true);
  };

  const handleEditSave = async () => {
    try {
      await api.patch(`/financial-records/${editRecord.id}/`, {
        donation_income: donationIncome,
      });
      fetchRecords();
      setEditDialogOpen(false);
    } catch (error) {
      console.error("Failed to update donation income.", error);
      Swal.fire("Error", "Failed to update donation income.", "error");
    }
  };

const handleDownloadPdf = () => {
  const doc = new jsPDF("landscape", "mm", "a4");
  const image = new Image();
  image.src = logo;

  image.onload = () => {
    // Logo and Heading
    doc.addImage(image, "JPEG", 14, 10, 30, 30);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(20);
    doc.text("Royal Priesthood Academy School System", 148, 18, { align: "center" });
    doc.setFont("helvetica", "normal");
    doc.setFontSize(11);
    doc.text("Zazay Community, Paynesville City, Monrovia", 148, 24, { align: "center" });
    doc.text("Contact: +231-770-216-549 / +231-770-405-998 / +231-886-484-807< | Email: schoolemail@gmail.com", 148, 30, { align: "center" });

    doc.setFontSize(12);
    doc.text(`Generated on: ${new Date().toLocaleString()}`, 14, 45);

    let y = 55;
    records.forEach((record, index) => {
      doc.setFont("helvetica", "bold");
      doc.setFontSize(12);
      doc.setTextColor(0, 102, 204);
      doc.text(`Record ${index + 1}`, 14, y);

      doc.setFont("helvetica", "normal");
      doc.setFontSize(10);
      doc.setTextColor(0);

      y += 6;
      doc.text(`Month: ${record.month}`, 14, y);
      doc.text(`Year: ${record.year}`, 80, y);
      doc.text(`Donation Income: $${record.donation_income}`, 140, y);
      y += 6;
      doc.text(`School Fees: $${record.school_fees_total}`, 14, y);
      doc.text(`Salaries: $${record.staff_salary_total}`, 80, y);
      doc.text(`Loans: $${record.staff_loan_total}`, 140, y);
      y += 6;
      doc.text(`Advances: $${record.salary_advance_total}`, 14, y);
      doc.text(`Expenditures: $${record.other_expenditures_total}`, 80, y);
      y += 6;
      doc.setFont("helvetica", "bold");
      doc.text(`Total Income: $${record.total_income}`, 14, y);
      doc.text(`Total Expenses: $${record.total_expenses}`, 80, y);
      doc.text(`Net Balance: $${record.net_balance}`, 140, y);

      y += 12;
      if (y > 180) {
        doc.addPage("landscape");
        y = 20;
      }
    });

    doc.setFontSize(10);
    doc.setTextColor(0);
    doc.text("Report From: The Finance Department", 148, 200, { align: "center" });

    doc.save("monthly_financial_records.pdf");
  };
};


const handleDownloadYearlyPdf = async () => {
  try {
    const res = await api.get("/yearly-report/");
    const yearlyRecords = res.data;

    const doc = new jsPDF("landscape", "mm", "a4");
    const image = new Image();
    image.src = logo;

    image.onload = () => {
      // Logo and Heading
      doc.addImage(image, "JPEG", 14, 10, 30, 30);
      doc.setFont("helvetica", "bold");
      doc.setFontSize(20);
      doc.text("Royal Priesthood Academy School System", 148, 18, { align: "center" });
      doc.setFont("helvetica", "normal");
      doc.setFontSize(11);
      doc.text("Zazay Community, Paynesville City, Monrovia", 148, 24, { align: "center" });
      doc.text("Contact: +231-770-216-549 / +231-770-405-998 / +231-886-484-807</ | salvationinchristministries69@gmail.com", 148, 30, { align: "center" });

      doc.setFontSize(12);
      doc.text(`Generated on: ${new Date().toLocaleString()}`, 14, 45);

      let y = 55;
      yearlyRecords.forEach((record, index) => {
        doc.setFont("helvetica", "bold");
        doc.setFontSize(12);
        doc.setTextColor(0, 102, 204);
        doc.text(`Yearly Record ${index + 1}`, 14, y);

        doc.setFont("helvetica", "normal");
        doc.setFontSize(10);
        doc.setTextColor(0);

        y += 6;
        doc.text(`Year: ${record.year}`, 14, y);
        doc.text(`Donation: $${record.donation_income}`, 80, y);
        doc.text(`School Fees: $${record.school_fees_total}`, 140, y);
        y += 6;
        doc.text(`Salaries: $${record.staff_salary_total}`, 14, y);
        doc.text(`Loans: $${record.staff_loan_total}`, 80, y);
        doc.text(`Advances: $${record.salary_advance_total}`, 140, y);
        y += 6;
        doc.text(`Expenditures: $${record.other_expenditures_total}`, 14, y);
        y += 6;
        doc.setFont("helvetica", "bold");
        doc.text(`Total Income: $${record.total_income}`, 14, y);
        doc.text(`Total Expenses: $${record.total_expenses}`, 80, y);
        doc.text(`Net Balance: $${record.net_balance}`, 140, y);

        y += 12;
        if (y > 180) {
          doc.addPage("landscape");
          y = 20;
        }
      });

      doc.setFontSize(10);
      doc.setTextColor(0);
      doc.text("Report From: The Finance Department", 148, 200, { align: "center" });

      doc.save("yearly_financial_records.pdf");
    };
  } catch (error) {
    console.error("Failed to fetch yearly records", error);
    Swal.fire("Error", "Failed to download yearly report", "error");
  }
};



  const filteredRecords = records.filter(
    (r) =>
      r.month.toLowerCase().includes(search.toLowerCase()) ||
      r.year.toString().includes(search)
  );

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  return (
    <Box p={2}>
      <Typography variant="h5" mb={2} fontWeight="bold">
        Monthly Financial Records
      </Typography>

      <Box
        display="flex"
        flexWrap="wrap"
        alignItems="center"
        gap={2}
        mb={3}
        p={2}
        sx={{ backgroundColor: "#f9f9f9", borderRadius: "8px" }}
      >
        <TextField
          label="Month"
          select
          SelectProps={{ native: true }}
          value={newMonth}
          onChange={(e) => setNewMonth(e.target.value)}
          sx={{ minWidth: 120 }}
        >
          <option value="">Select Month</option>
          {[
            "January",
            "February",
            "March",
            "April",
            "May",
            "June",
            "July",
            "August",
            "September",
            "October",
            "November",
            "December",
          ].map((month) => (
            <option key={month} value={month}>
              {month}
            </option>
          ))}
        </TextField>
        <TextField
          label="Year"
          type="number"
          value={newYear}
          onChange={(e) => setNewYear(e.target.value)}
          sx={{ minWidth: 100 }}
        />
        <TextField
          label="Donation Income"
          type="number"
          value={newDonation}
          onChange={(e) => setNewDonation(e.target.value)}
          sx={{ minWidth: 160 }}
        />
        <Button variant="contained" onClick={handleCreate} disabled={submitting}>
          {submitting ? <CircularProgress size={24} /> : "Add Record"}
        </Button>
      </Box>

      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2} flexWrap="wrap" gap={2}>
        <TextField
          label="Search Month or Year"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          sx={{ minWidth: 240 }}
        />
        <Box display="flex" gap={2} flexWrap="wrap">
          <Button
            variant="contained"
            color="primary"
            onClick={handleDownloadPdf}
            startIcon={<PictureAsPdf />}
          >
            Download Monthly PDF
          </Button>
          <Button
            variant="contained"
            color="secondary"
            onClick={handleDownloadYearlyPdf}
            startIcon={<PictureAsPdf />}
          >
            Download Yearly PDF
          </Button>
        </Box>
      </Box>

      {loading ? (
        <CircularProgress />
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead sx={{ backgroundColor: "#f5f5f5" }}>
              <TableRow>
                <TableCell>Month</TableCell>
                <TableCell>Year</TableCell>
                <TableCell>Donation</TableCell>
                <TableCell>School Fees</TableCell>
                <TableCell>Salaries</TableCell>
                <TableCell>Loans</TableCell>
                <TableCell>Advances</TableCell>
                <TableCell>Expenditures</TableCell>
                <TableCell sx={{ backgroundColor: "#530101ff", color: "#ffffff" }}>
                  Income
                </TableCell>
                <TableCell sx={{ backgroundColor: "#530101ff", color: "#ffffff" }}>
                  Expenses
                </TableCell>
                <TableCell sx={{ backgroundColor: "#530101ff", color: "#ffffff" }}>
                  Balance
                </TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredRecords
                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                .map((record) => (
                  <TableRow key={record.id}>
                    <TableCell>{record.month}</TableCell>
                    <TableCell>{record.year}</TableCell>
                    <TableCell>${record.donation_income}</TableCell>
                    <TableCell>${record.school_fees_total}</TableCell>
                    <TableCell>${record.staff_salary_total}</TableCell>
                    <TableCell>${record.staff_loan_total}</TableCell>
                    <TableCell>${record.salary_advance_total}</TableCell>
                    <TableCell>${record.other_expenditures_total}</TableCell>
                    <TableCell sx={{ backgroundColor: "#013953ff", color: "#ffffff" }}>
                      ${record.total_income}
                    </TableCell>
                    <TableCell sx={{ backgroundColor: "#001f3f", color: "#ffffff" }}>
                      ${record.total_expenses}
                    </TableCell>
                    <TableCell sx={{ backgroundColor: "#013953ff", color: "#ffffff" }}>
                      ${record.net_balance}
                    </TableCell>
                    <TableCell>
                      <IconButton color="primary" onClick={() => handleEditClick(record)}>
                        <Edit />
                      </IconButton>
                      <IconButton color="error" onClick={() => handleDelete(record.id)}>
                        <Delete />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
            </TableBody>
          </Table>
          <TablePagination
            component="div"
            count={filteredRecords.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
          />
        </TableContainer>
      )}

      <Box mt={5}>
        <Typography variant="h6" gutterBottom>
          Monthly Summary Bar Chart
        </Typography>
        <ResponsiveContainer width="100%" height={300}>
          <BarChart
            data={records}
            margin={{ top: 20, right: 30, left: 0, bottom: 5 }}
          >
            <XAxis dataKey="month" />
            <YAxis />
            <Tooltip />
            <Legend />
            <Bar dataKey="total_income" fill="#4caf50" name="Total Income" />
            <Bar dataKey="total_expenses" fill="#f44336" name="Total Expenses" />
            <Bar dataKey="net_balance" fill="#2196f3" name="Net Balance" />
          </BarChart>
        </ResponsiveContainer>
      </Box>

      {/* Edit Dialog */}
      <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)}>
        <DialogTitle>Edit Donation Income</DialogTitle>
        <DialogContent>
          <TextField
            label="Donation Income"
            type="number"
            fullWidth
            value={donationIncome}
            onChange={(e) => setDonationIncome(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleEditSave}>
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default MonthlyFinancialRecord;











